/* 資料可攜：把整個知識庫匯出成「不依賴這個 App 也能讀」的形式。
   你哪天不想用它了，或只想用記事本打開存檔 —— 這條路必須存在。

   格式：
     README.md          說明這包是什麼、怎麼讀
     knowledge.json     完整原始資料（可再導入）
     concepts/*.md      每條知識一個 Markdown 檔（人看的）
     attachments/*      托盤裡還沒編目的素材
*/
import type { Category, Concept, InboxItem, KnowledgeLink } from "../domain/types.ts";
import { deriveTitle } from "../domain/rules.ts";
import type { ZipFile } from "./zipWrite.ts";

export interface PortableInput {
  concepts: Concept[];
  categories: Category[];
  links: KnowledgeLink[];
  inbox?: InboxItem[];
  attachments?: { id: string; name: string; mime: string; bytes: Uint8Array }[];
  exportedAt?: number;
}

function safeName(text: string): string {
  return text.replace(/[\\/:*?"<>|\n\r\t]/g, "_").replace(/\s+/g, " ").trim().slice(0, 60) || "未命名";
}

function conceptMarkdown(c: Concept, categoryName: string, outLinks: string[], inLinks: string[]): string {
  const lines = [
    "---",
    "id: " + c.id,
    "標題: " + (c.title || deriveTitle(c.summary)),
    "主類別: " + categoryName,
    "狀態: " + c.status,
    "來源: " + (c.source || ""),
    "到期: " + (c.reviewDueAt ? new Date(c.reviewDueAt).toISOString().slice(0, 10) : ""),
    "複習間隔天數: " + c.reviewIntervalDays,
    "成人知識: " + (c.nsfw ? "是" : "否"),
    "版本: " + c.rev,
    "建立: " + new Date(c.createdAt).toISOString(),
    "更新: " + new Date(c.updatedAt).toISOString(),
    "---",
    "",
    "# " + (c.title || deriveTitle(c.summary)),
    "",
    "## 兩句摘要",
    "",
    c.summary,
    "",
    "## 我為什麼要存它（採集標籤）",
    "",
    c.note || "（未填寫）",
    "",
  ];
  if (c.body) lines.push("## 正文", "", c.body, "");
  if (outLinks.length || inLinks.length) {
    lines.push("## 棉線", "");
    for (const l of outLinks) lines.push("- → " + l);
    for (const l of inLinks) lines.push("- ← " + l);
    lines.push("");
  }
  return lines.join("\n");
}

export function buildPortableFiles(input: PortableInput): ZipFile[] {
  const encoder = new TextEncoder();
  const at = input.exportedAt ?? Date.now();
  const categoryName = (id: string) => input.categories.find((c) => c.id === id)?.name ?? "（未分類）";
  const byId = new Map(input.concepts.map((c) => [c.id, c]));
  const files: ZipFile[] = [];

  const ordered = [...input.concepts].sort((a, b) => a.createdAt - b.createdAt);
  ordered.forEach((c, index) => {
    const out = input.links.filter((l) => l.fromId === c.id)
      .map((l) => l.type + "：" + (byId.get(l.toId) ? deriveTitle(byId.get(l.toId)!.summary) : "（已刪除或受限）"));
    const into = input.links.filter((l) => l.toId === c.id)
      .map((l) => l.type + "：" + (byId.get(l.fromId) ? deriveTitle(byId.get(l.fromId)!.summary) : "（已刪除或受限）"));
    const number = String(index + 1).padStart(3, "0");
    files.push({
      name: "concepts/" + number + "-" + safeName(c.title || deriveTitle(c.summary)) + ".md",
      data: encoder.encode(conceptMarkdown(c, categoryName(c.primaryCategoryId), out, into)),
    });
  });

  files.push({
    name: "knowledge.json",
    data: encoder.encode(JSON.stringify({
      format: "lifesage-portable",
      version: 1,
      exportedAt: new Date(at).toISOString(),
      counts: { concepts: input.concepts.length, categories: input.categories.length,
        links: input.links.length, attachments: input.attachments?.length ?? 0 },
      concepts: input.concepts, categories: input.categories, links: input.links,
      inbox: (input.inbox ?? []).map((i) => ({ ...i, blobRef: i.blobRef })),
    }, null, 2)),
  });

  const readme = [
    "# 人生智囊 LifeSage — 資料匯出",
    "",
    "這包是你自己的資料，不是備份檔，也不是專有格式。",
    "",
    "| 檔案 | 內容 |",
    "|---|---|",
    "| concepts/*.md | 每條知識一個 Markdown 檔，用任何編輯器都讀得懂 |",
    "| knowledge.json | 完整原始資料（含版本、類別、棉線）—— 再導入時用這一份 |",
    "| attachments/* | 托盤裡還沒編目的素材本體（如果有） |",
    "",
    "## 讀法",
    "",
    "每條知識的開頭是 YAML 形式的資訊（id、類別、到期日、來源、版本），",
    "接著是「兩句摘要」、「我為什麼要存它」，最後才是正文與棉線。",
    "",
    "摘要之所以只有兩句，是刻意的：一句話講不清楚的，通常代表那是兩條知識。",
    "",
    "## 匯出時間",
    "",
    new Date(at).toISOString() + "（共 " + input.concepts.length + " 條知識、"
      + input.links.length + " 條棉線）",
    "",
  ].join("\n");
  files.push({ name: "README.md", data: encoder.encode(readme) });

  for (const file of input.attachments ?? []) {
    files.push({ name: "attachments/" + safeName(file.name), data: file.bytes });
  }
  return files;
}

export function exportFileName(at = Date.now()): string {
  return "人生智囊-匯出-" + new Date(at).toISOString().slice(0, 10) + ".zip";
}
